import { getFirebase } from '@assets/firebase/FirebaseAdapater';
import { DataAccessUtils } from '@assets/model/DataAccessUtils';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import _ from 'lodash';
import { autorun, computed, observable, runInAction } from 'mobx';
import moment from 'moment';
import { useEffect } from 'react';
import { ChatMessageData, RagAPI } from './RagAPI';
import { RuleType } from './ScoreUtils';

function isString(s) {
    return typeof s === 'string' || s instanceof String;
}

interface StockInfo {
    ticker: string;
    current: number;
    eps: number;
    avg_over_year: number;
}

interface TranscriptEntry {
    highlight: string;
    source_text: string;
}
interface KPI {
    kpi: string;
    value: string;
}

interface YearlyFinance {
    cogs: string;
    ebitda: string;
    revenue: string;
    year: string;
    ebitda_margin: string;
    yoy_growth: string;
}
interface KeyPerson {
    name: string;
    position: string;
    started_job_year: string;
    bio: string;
}

interface Segment {
    revenue: string;
    service: string;
    segment: string;
    share: string;
    short_description: string;
}

interface ActiveJob {
    jobId: string;
    fileName: string;
    status: string;
    task: string;
    progress: number;
}

interface UserInfo {
    isAdmin: boolean;
}

interface UserInfo {
    isAdmin: boolean;
}

type ServiceFileSummary = {
    brief_overview: string;
    one_sentence_overview: string;
    main_points: string[];
    topics: string[];
    risks_mentioned: string[];
    merits_mentioned: string[];
    created_at?: string;
    author?: string;
};

type ServiceFileStatus = 'completed' | 'pending' | 'processing' | 'error';
export type ServiceFileEntry = {
    filename: string;
    saveKey: string;
    id: string;
    summary?: ServiceFileSummary;
    url: string;
    status?: ServiceFileStatus;
    ragStatus?: ServiceFileStatus;
};
type ServiceFiles = { files: { [fileId: string]: ServiceFileEntry } };
type ServiceFileLookup = { [serviceId: string]: ServiceFiles };

type FirmInfo = {
    logoURL?: string;
};

interface ModalDisplay {
    title: string;
    transcript: TranscriptEntry;
}

class Store {
    constructor() {}

    @observable
    accessor selectedReportId: string = '';

    @observable
    accessor reportDataDB: any = null;

    @observable
    accessor settings: any = {};

    @computed get scoringOverideRules(): {
        [sectionId: string]: { [field: string]: RuleType };
    } {
        if (!this.settings) return {};

        return this.settings.scoringOverideRules ?? {};
    }

    @computed get isAdmin() {
        if (!this.users) return false;

        return this.users[this.userId]?.isAdmin ?? false;
    }
    @computed get showExportButton() {
        if (!this.isAdmin) return false;

        return true;
    }

    @computed get showRerunReport() {
        if (!this.isAdmin) return false;

        return true;
    }

    @computed get showDeleteButton() {
        if (!this.isAdmin) return false;

        return true;
    }

    @observable
    accessor reportDataOverridesDB: any = {};

    @observable accessor reportsDataAccessCache: {
        [reportId: string]: DataAccessUtils;
    } = {};

    @observable
    accessor users: { [userId: string]: UserInfo } = {};

    @observable accessor userId: string;

    @observable
    accessor jobs: any = {};

    @observable
    accessor systemStatus: any = {};

    @observable accessor activeJob: ActiveJob = null;

    @observable accessor areOveridesLoaded: boolean = false;
    @observable accessor areScoreRulesLoaded: boolean = false;

    @observable accessor allProjectFiles: ServiceFileLookup = {};

    @observable accessor firmInfo: FirmInfo = {};

    @observable accessor isChatLoaded: boolean = false;
    @observable accessor chatData: { [reportId: string]: ChatMessageData[] } =
        {};

    @observable accessor ragDB: any = {};
    @observable accessor isRagDbLoaded: boolean = false;

    @observable accessor lastReportLoaded: string = '';

    @computed get activeReportOriginal() {
        const ticker = this.selectedReportId;
        if (!this.reportDataDB) {
            return null;
        }

        if (this.reportDataDB.hasOwnProperty(ticker)) {
            if (this.reportDataDB[ticker].status == 'processing') {
                return null;
            }

            return _.cloneDeep(this.reportDataDB[ticker]);
        }

        return null;
    }

    @computed get isLoaded() {
        return (
            this.reportDataDB != null &&
            this.areOveridesLoaded &&
            this.areScoreRulesLoaded
        );
    }

    @computed get projectFiles(): ServiceFiles {
        return this.allProjectFiles[this.selectedReportId] ?? { files: {} };
    }

    @computed get reportChatHistory() {
        return (
            this.chatData[this.selectedReportId] ?? [
                {
                    isMe: false,
                    message: 'Hello! How can I help you today?',
                    date: new Date(),
                    processed: true
                }
            ]
        );
    }

    @computed get reportOverride() {
        const ticker = this.selectedReportId;
        var over_rides = {};
        if (
            this.reportDataOverridesDB &&
            this.reportDataOverridesDB.hasOwnProperty(ticker)
        ) {
            over_rides = this.reportDataOverridesDB[ticker];
        }
        return over_rides;
    }

    @computed
    get query(): DataAccessUtils {
        if (!this.activeReportOriginal) {
            return null;
        }

        if (
            !this.areOveridesLoaded ||
            !this.isRagDbLoaded ||
            !this.isChatLoaded
        ) {
            return null;
        }

        return new DataAccessUtils(
            this.activeReportOriginal,
            this.reportOverride,
            this.selectedReportId
        );
    }

    @computed
    get ragAPI(): RagAPI {
        if (!this.activeReportOriginal) {
            return null;
        }
        return new RagAPI(this.selectedReportId);
    }

    resetDataAccessCache() {
        this.reportsDataAccessCache = {};
    }

    createDataAccess(reportId: string) {
        if (this.reportsDataAccessCache[reportId]) {
            return this.reportsDataAccessCache[reportId];
        }

        //console.log('Creating data access for: ', reportId);
        const deal = this.reportDataDB[reportId]; //Fix over rides ?
        if (!deal || deal['status'] != 'completed') {
            console.log('No data found for: ', reportId);
            return null;
        }

        const override = this.reportDataOverridesDB[reportId];
        this.reportsDataAccessCache[reportId] = new DataAccessUtils(
            deal,
            override,
            reportId
        );
        return this.reportsDataAccessCache[reportId];
    }

    @computed
    get company_name() {
        return this.query?.value('company_name');
    }

    @computed
    get ticker() {
        return this.query?.value('ticker');
    }

    @computed get reportCreationDate() {
        if (!this.query?.value('created')) return null;

        return moment.unix(this.query?.value('created')).format('YYYY-MM-D');
    }

    @computed get errorMessageTicker() {
        if (!this.query?.value('error')) return null;
        return this.query?.value('error');
    }
    @computed get reportFiscalYear() {
        if (!this.query?.value('report_fiscal_year')) return null;

        return this.query?.value('report_fiscal_year');
    }

    @computed get companyOverview() {
        return this.query?.value('company_overview');
    }
}

export enum UIDealDetailsTabs {
    DASHBOARD = 'DASHBOARD',
    DETAILS = 'DETAILS',
    COMPARE = 'COMPARE',
    SCORE_BREAKDOWN = 'SUMMARY',
    CHAT = 'CHAT'
}

export type UIScrolling = 'UP' | 'DOWN' | 'NONE';
type CategoryType = 'category' | 'sub_category';
export type UICategoryFilter = { type: CategoryType; value: string };

export class StoreUI {
    get isDev() {
        return process.env.NODE_ENV === 'development';
    }

    resetViewerRequests() {
        this.pdfRequest = { page: -1, highlightTexts: [] };
    }
    resetDealView() {
        this.dealViewTab = UIDealDetailsTabs.DASHBOARD;
        if (this.isDev) {
            this.dealViewTab = UIDealDetailsTabs.DETAILS;
        }

        this.resetViewerRequests();
    }

    resetHome() {
        this.dealPageNameSearch = '';
        this.dealPageIndustrySearch = [];
        this.dealPageScoreSearch = [];
    }

    onKeyUpReset() {
        this.isKeyControlKeyDown = false;
        this.scrolling = 'NONE';
        this.isKeyTabDown = false;
        this.isKeyShiftKeyDown = false;
        this.isKeyEscapeDown = false;
    }

    requestPdfImagePage(page: number) {
        this.resetViewerRequests();
        setTimeout(() => {
            this.pdfRequest = {
                page,
                highlightTexts: []
            };
        }, 10);
    }

    constructor() {
        this.resetDealView();
    }

    @observable accessor detailsViewYearsToShow = 6;

    @observable accessor pdfRequest: {
        page: number;
        highlightTexts?: string[];
    };

    @observable accessor disableLoading = false;
    @observable accessor scrolling: UIScrolling = 'NONE';

    @observable accessor currentSplitRatioDetailView = 50;
    @observable
    accessor modal: ModalDisplay = null;

    @observable accessor dealPageNameSearch: string = '';
    @observable accessor dealPageIndustrySearch: UICategoryFilter[] = [];
    @observable accessor dealPageScoreSearch: string[] = [];

    @observable accessor pageTitle = '';

    @observable accessor dealViewTab: UIDealDetailsTabs;

    @observable accessor chatWaitingForResponse: boolean = false;

    @observable accessor isKeyControlKeyDown: boolean = false;

    @observable accessor isKeyTabDown: boolean = false;
    @observable accessor isKeyShiftKeyDown: boolean = false;
    @observable accessor isKeyEscapeDown: boolean = false;
}

const store = new Store();
const storeUI = new StoreUI();

export const useStore = () => store;
export const useStoreUI = () => storeUI;

export const useSetPageTitle = (pageTitle: string) => {
    useEffect(() => {
        storeUI.pageTitle = pageTitle;
    }, [pageTitle]);
};
